// File: src/components/EntryExtrasFields.js
// The extras an organizer asks about, as an entrant fills them in.
//
// Shared by the entry form and the manage view, because an entrant changing
// their lunch count later has to see the same three controls they answered the
// first time - and a second copy of this would be a second place for yes/no to
// stop meaning 1/0.
//
// The question text is the organizer's own words and is rendered as stored.
// Only the scaffolding around it is translated.

import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

// What counts as a yes, and which questions are therefore being asked.
//
// Mirrors parseEntryAnswers in backend/entries.js. The server decides this for
// itself and never trusts what arrives - this copy exists so the form does not
// show a field that would be thrown away, not to be the authority.
//
// One level deep by construction: a trigger question can never itself be
// conditional, so this needs no recursion and cannot loop.
const isYes = (value) => value === true || value === 1 || value === '1' || value === 'true';

export const isAsked = (question, answers, questions) => {
  if (!question.depends_on_question_id) return true;
  const trigger = (questions || []).find(q => q.id === question.depends_on_question_id);
  if (!trigger) return true;
  return isYes(answers[trigger.id]);
};

// Answers keyed by question id, in the shape the API takes:
//
//   { 3: true, 4: '2', 5: 'nuts' }  ->  [{ question_id: 3, value: true }, ...]
//
// A yes/no is always sent, because an unticked box is the real answer "no", not
// an absence. The other two are sent only when filled: no row means not
// answered, and an empty string would write one that says nothing.
export const answersToPayload = (answers, questions) => questions
  .filter(q => isAsked(q, answers, questions))
  .filter(q => q.answer_type === 'yes_no'
    || (answers[q.id] !== undefined && answers[q.id] !== ''))
  .map(q => ({ question_id: q.id, value: answers[q.id] ?? (q.answer_type === 'yes_no' ? false : '') }));

// Turns the API's answer rows back into that map, for the manage view.
export const answersFromEntry = (rows) => (rows || []).reduce((byId, row) => {
  byId[row.question_id] = row.answer_type === 'text'
    ? (row.answer_text || '')
    : (row.answer_type === 'yes_no' ? row.answer_number === 1 : String(row.answer_number ?? ''));
  return byId;
}, {});

function EntryExtrasFields({ questions, answers, onChange }) {
  const { t } = useLanguage();

  if (!questions || questions.length === 0) return null;

  const set = (id, value) => onChange({ ...answers, [id]: value });

  // A question whose trigger is not ticked is not asked. Its own answer stays
  // in state rather than being cleared, so ticking the trigger back on returns
  // what they had typed - and the server ignores anything hidden either way.
  const shown = questions.filter(q => isAsked(q, answers, questions));
  if (shown.length === 0) return null;

  return (
    <div>
      <label className="block text-ink-muted mb-2 font-semibold">{t('entryExtras')}</label>
      <p className="text-ink-muted text-sm mb-2">{t('entryExtrasHelp')}</p>

      <div className="space-y-2">
        {shown.map(question => (
          <div
            key={question.id}
            className={`bg-paper-alt px-3 py-2 rounded${
              question.depends_on_question_id ? ' ml-6' : ''}`}
          >
            {question.answer_type === 'yes_no' ? (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(answers[question.id])}
                  onChange={(e) => set(question.id, e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-ink flex-1">
                  {question.label}
                  {question.required ? ' *' : ''}
                </span>
              </label>
            ) : (
              <>
                <label
                  className="block text-ink mb-1"
                  htmlFor={`extra-${question.id}`}
                >
                  {question.label}
                  {question.required ? ' *' : ''}
                </label>
                <input
                  id={`extra-${question.id}`}
                  type={question.answer_type === 'number' ? 'number' : 'text'}
                  // Bounds the organizer set, handed to the browser as well as
                  // to the server - a spinner that stops at 6 explains the
                  // limit better than a refusal after submitting does.
                  min={question.answer_type === 'number' && question.min_value !== null
                    ? question.min_value : undefined}
                  max={question.answer_type === 'number' && question.max_value !== null
                    ? question.max_value : undefined}
                  value={answers[question.id] ?? ''}
                  onChange={(e) => set(question.id, e.target.value)}
                  className="w-full px-3 py-1.5 bg-paper border border-line rounded text-ink"
                />
              </>
            )}

            {question.help_text && (
              <p className="text-ink-muted text-sm mt-1">{question.help_text}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default EntryExtrasFields;
